import { useEffect, useRef } from "react";
import { TRADING_NAME } from "@/constants/company";

interface ProposalHtmlPreviewProps {
  html: string;
  title?: string;
  className?: string;
}

export function ProposalHtmlPreview({
  html,
  title = `${TRADING_NAME} energy plan proposal`,
  className = "",
}: ProposalHtmlPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        const height = doc?.documentElement?.scrollHeight ?? 900;
        iframe.style.height = `${Math.max(height, 480)}px`;
      } catch {
        iframe.style.height = "900px";
      }
    };

    // A srcdoc frame's address is the parent page, so href="#section" loads
    // /plan inside the sandbox and the preview goes blank. Drop the href and
    // scroll from this page instead. Do not use instanceof here: the link
    // belongs to the frame's document, so it is not a parent Element.
    const scrollToSection = (event: Event) => {
      const anchor = event.currentTarget as Element | null;
      const id = anchor?.getAttribute("data-section");
      const section = id ? iframe.contentDocument?.getElementById(id) : null;
      if (!section) return;
      event.preventDefault();
      const top = window.scrollY
        + iframe.getBoundingClientRect().top
        + section.getBoundingClientRect().top
        - 24;
      window.scrollTo({ top, behavior: "smooth" });
    };

    const bind = () => {
      resize();
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.querySelectorAll("a[href^='#'], a[data-section]").forEach((node) => {
        const anchor = node as Element;
        if (anchor.getAttribute("data-bound") === "1") return;
        const href = anchor.getAttribute("href") ?? "";
        const id = anchor.getAttribute("data-section") || (href.startsWith("#") ? href.slice(1) : "");
        if (!id) return;
        anchor.setAttribute("data-section", id);
        anchor.setAttribute("data-bound", "1");
        anchor.removeAttribute("href");
        anchor.addEventListener("click", scrollToSection);
      });
    };

    iframe.addEventListener("load", bind);
    bind();
    return () => {
      iframe.removeEventListener("load", bind);
      iframe.contentDocument?.querySelectorAll("a[data-section]").forEach((anchor) => {
        anchor.removeEventListener("click", scrollToSection);
      });
    };
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={html}
      sandbox="allow-same-origin"
      className={`w-full rounded-2xl border border-white/10 bg-white ${className}`}
      style={{ minHeight: 480 }}
    />
  );
}
